import { PropsWithChildren, ReactElement, ReactNode } from 'react';

import classNames from 'classnames';

import { Card, Flex, Link, Text } from '@spotify-internal/encore-web';

import { useAuth } from '../../behaviors/auth-factory';
import { useSupportMessenger } from '../../hooks/useSupportMessenger';

import styles from './NotFoundContent.module.css';

const COPY_BY_VARIANT = {
  page: {
    title: 'Page not found',
    description:
      "Oops! Looks like you've ventured off the beaten path. The page you're looking for might have been moved, deleted, or never existed. Don't worry, it happens to the best of us!",
  },
  resource: {
    title: 'Resource not found',
    description:
      "Oops! The resource you're looking for might have been moved, deleted, or never existed. Don't worry, it happens to the best of us!",
  },
} as const;

type NotFoundCardContent = {
  title: ReactNode;
  description: ReactNode;
  action?: ReactNode;
};

export type NotFoundContentProps = PropsWithChildren<{
  variant?: 'page' | 'resource';
  fillViewport?: boolean;
}>;

export type NotFoundRecoveryProps = PropsWithChildren<
  Omit<NotFoundCardContent, 'action'>
>;

const NotFoundCard = ({
  id,
  title,
  description,
  action,
}: NotFoundCardContent & { id: string }): ReactElement => (
  <Card
    id={id}
    variant="tinted"
    padding="base"
    title={title}
    body={
      <Flex direction="column" alignItems="start" gap="tighter">
        <Text variant="bodyMedium" as="p" semanticColor="textSubdued">
          {description}
        </Text>
        {action}
      </Flex>
    }
  />
);

const Recovery = ({
  title,
  description,
  children,
}: NotFoundRecoveryProps): ReactElement => (
  <NotFoundCard
    id="not-found-recovery"
    title={title}
    description={description}
    action={children}
  />
);

const DefaultRecovery = (): ReactElement => (
  <Recovery
    title="Double-check the URL"
    description="Sometimes a simple typo is the culprit"
  />
);

const NotFoundContent = ({
  variant = 'page',
  fillViewport = true,
  children,
}: NotFoundContentProps = {}): ReactElement => {
  const { user } = useAuth();
  const { openChat } = useSupportMessenger();

  const { title, description } = COPY_BY_VARIANT[variant];

  const handleContactSupport = () => {
    openChat(
      user?.name && user?.email
        ? { name: user.name, email: user.email }
        : undefined,
    );
  };

  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      width="100%"
      className={classNames({ [styles.fillViewport]: fillViewport })}
    >
      <Flex
        direction="column"
        justifyContent="center"
        alignItems="center"
        gap="looser"
      >
        <Flex direction="column" alignItems="center">
          <Text variant="bodySmallBold" semanticColor="textSubdued">
            Error 404
          </Text>
          <Text variant="titleSmall" as="h2">
            {title}
          </Text>
        </Flex>
        <Text variant="bodyMedium" as="p" className={styles.description}>
          {description}
        </Text>
        <Flex gap="looser" className={styles.cards}>
          {children ?? <DefaultRecovery />}
          <NotFoundCard
            id="not-found-start-fresh"
            title="Start fresh"
            description={
              <>
                <Link href="/" data-track-source-anchor="647f57b009">Go back to the homepage</Link> and find what you
                need
              </>
            }
          />
          <NotFoundCard
            id="not-found-need-help"
            title="Need help?"
            description={
              <>
                If you think this is an error, please{' '}
                <Link
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    handleContactSupport();
                  }}
                 data-track-source-anchor="e4eab3a3c3">
                  contact our support team
                </Link>
              </>
            }
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

const ComposedNotFoundContent = Object.assign(NotFoundContent, {
  Recovery,
  DefaultRecovery,
});

export { ComposedNotFoundContent as NotFoundContent };
